import { Decimal } from '../../datastructures';
import { assetTokenRatesAtom } from '../lendAndBorrow';
import poolsUserBalanceAtom from '../pool/poolsUserBalanceAtom';
import { walletTokenBalancesAtom } from '../wallet';
import { poolTokenRatesAtom } from '../pool';
import { createAsyncAtom } from '../../lib/state';
import lendAndBorrowPositionsAtom from './lendAndBorrowPositions';
import avnuSupportedTokensAtom from '../swap/avnuSupportedTokensAtom';
import { buildAvnuAddressToSymbolMap, buildConfigTickerToPriceMap, resolveTokenPrice } from '../../utils/tokenPriceResolver';

export interface UserNetWorth {
  lend: Decimal;
  debt: Decimal;
  pools: Decimal;
  wallet: Decimal;
}

const userNetWorthAtom = createAsyncAtom(async get => {
  const lendAndBorrowPositions = await get(lendAndBorrowPositionsAtom);
  const poolPositions = await get(poolsUserBalanceAtom);
  const walletTokenBalances = await get(walletTokenBalancesAtom);
  const poolTokenRates = await get(poolTokenRatesAtom);
  const assetTokenRates = await get(assetTokenRatesAtom);
  const avnuSupportedTokens = await get(avnuSupportedTokensAtom);

  // Don't return partial net worth — wait until all data sources are ready
  if (!lendAndBorrowPositions) {
    return null;
  }

  let netWorthLend = Decimal.ZERO;
  let netWorthDebt = Decimal.ZERO;
  Object.entries(lendAndBorrowPositions).forEach(([, accountPositions]) => {
    accountPositions.forEach(position => {
      netWorthLend = netWorthLend.add(position.lendBalanceUsd);
      netWorthDebt = netWorthDebt.add(position.debtBalanceUsd);
    });
  });

  let netWorthPools = Decimal.ZERO;
  Object.entries(poolPositions).forEach(([, value]) => {
    netWorthPools = netWorthPools.add(value);
  });

  const configTickerToPrice = buildConfigTickerToPriceMap(poolTokenRates, assetTokenRates.rate);
  const avnuAddressToSymbol = buildAvnuAddressToSymbolMap(avnuSupportedTokens);

  let wallet = Decimal.ZERO;
  Object.entries(walletTokenBalances).forEach(([token, balance]) => {
    const symbol = avnuAddressToSymbol[token];
    const price = resolveTokenPrice(token, symbol, poolTokenRates, assetTokenRates.rate, configTickerToPrice) ?? Decimal.ZERO;

    wallet = wallet.add(balance.mul(price));
  });

  return {
    lend: netWorthLend,
    debt: netWorthDebt,
    pools: netWorthPools,
    wallet,
  };
});

export default userNetWorthAtom;
